import { useEffect, useState } from 'react'
import { useAuth } from '../../lib/auth'
import { useProfile, useUpdateDisplayName } from '../../hooks/useProfile'
import { useToast } from '../../hooks/useToast'
import { supabase } from '../../lib/supabase'
import { isExtensionInstalled, getExtensionVersion } from '../../lib/extension'
import { initialsFromUser } from '../../lib/initials'
import { downloadMyData } from '../../lib/export'

const DISPLAY_NAME_MAX = 100

// Account-deletion UI intentionally not rendered. The `delete_my_account`
// RPC exists and clears public-schema data, but there's no Edge Function
// yet to clear `auth.users` — so clicking "Delete" would leave a zombie
// auth account (empty profile, no team) that users could sign back into.
// Rather than ship half a deletion flow, the UI is removed until the
// completion path is built. See docs/adr/0038-account-deletion-deferred.md
// for the decision + what it'd take to re-enable.

export default function AccountSettings() {
  const { user, signOut } = useAuth()
  const { profile, profileLoading, profileError, refreshProfile } = useProfile()
  const updateName = useUpdateDisplayName()
  const [exportBusy, setExportBusy] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const [displayName, setDisplayName] = useState('')
  const [nameDirty, setNameDirty] = useState(false)
  const [savingName, setSavingName] = useState(false)

  // Sync input with profile when it loads (and when not in the middle of editing)
  useEffect(() => {
    if (profile && !nameDirty) setDisplayName(profile.display_name || '')
  }, [profile, nameDirty])

  const [newEmail, setNewEmail] = useState('')
  const [emailStage, setEmailStage] = useState<'idle' | 'sent' | 'confirmed'>('idle')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [emailBusy, setEmailBusy] = useState(false)

  // If the USER_UPDATED event fires with the new email confirmed, transition the UI.
  useEffect(() => {
    if (emailStage === 'sent' && user?.email && newEmail && user.email.toLowerCase() === newEmail.trim().toLowerCase()) {
      setEmailStage('confirmed')
    }
  }, [user?.email, newEmail, emailStage])

  const { toast, show: showToast } = useToast()

  const handleSaveName = async () => {
    if (!user) return
    const trimmed = displayName.trim()
    if (!trimmed || !nameDirty) return
    setSavingName(true)
    try {
      await updateName.mutateAsync(trimmed)
      setNameDirty(false)
      showToast('Name updated')
      // RealtimeBridge will invalidate via postgres_changes; refresh defensively
      refreshProfile()
    } catch (err) {
      showToast(`Failed to save name: ${(err as Error).message}`)
    } finally {
      setSavingName(false)
    }
  }

  const handleEmailChange = async () => {
    const cleaned = newEmail.trim().toLowerCase()
    const currentEmail = user?.email?.toLowerCase()
    if (!cleaned || cleaned === currentEmail) return
    setEmailBusy(true)
    setEmailError(null)
    const { error } = await supabase.auth.updateUser({ email: cleaned })
    setEmailBusy(false)
    if (error) {
      setEmailError(error.message)
    } else {
      setEmailStage('sent')
    }
  }

  const resetEmailFlow = () => {
    setNewEmail('')
    setEmailStage('idle')
    setEmailError(null)
  }

  // Parse linkedin_profile_urn into a readable link if possible
  const linkedinUrl = profile?.linkedin_profile_urn
    ? `https://www.linkedin.com/in/${profile.linkedin_profile_urn.replace(/^urn:li:fsd?_profile:/, '')}`
    : null

  const initials = initialsFromUser(profile?.display_name || user?.email)

  // isExtensionInstalled() reads the DOM; resolve once per render so the
  // badge label and version line can't disagree if the extension injects
  // its marker between the two reads.
  const extInstalled = isExtensionInstalled()
  const extVersion = extInstalled ? getExtensionVersion() : null

  if (profileError) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6">
        <h2 className="text-sm font-semibold text-red-700">Couldn't load your profile</h2>
        <p className="mt-1 text-xs text-red-600">{profileError.message}</p>
        <button
          onClick={() => { refreshProfile() }}
          className="mt-3 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="relative flex flex-col gap-6">
      <header>
        <h1 className="text-lg font-semibold text-zinc-900">Account</h1>
        <p className="text-sm text-zinc-500">Manage your profile and sign-in details.</p>
      </header>

      {/* Profile */}
      <section className="rounded-xl border border-zinc-200 bg-white p-6">
        <h2 className="text-sm font-semibold text-zinc-900">Profile</h2>
        <div className="mt-4 flex items-center gap-4">
          {profile?.avatar_url ? (
            <img src={profile.avatar_url} alt="" className="h-14 w-14 rounded-full object-cover" />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600">
              {profileLoading ? '...' : initials}
            </div>
          )}
          <div className="flex-1">
            <label className="block text-xs font-medium text-zinc-500">Display name</label>
            <form
              className="mt-1 flex gap-2"
              onSubmit={(e) => { e.preventDefault(); handleSaveName() }}
            >
              <input
                value={displayName}
                onChange={(e) => { setDisplayName(e.target.value); setNameDirty(true) }}
                maxLength={DISPLAY_NAME_MAX}
                disabled={profileLoading}
                className="flex-1 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm focus:border-zinc-400 focus:outline-none disabled:bg-zinc-50"
              />
              <button
                type="submit"
                disabled={!nameDirty || savingName || !displayName.trim() || profileLoading}
                className="rounded-md bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-40"
              >
                {savingName ? 'Saving...' : 'Save'}
              </button>
            </form>
            <p className="mt-2 text-xs text-zinc-400">{user?.email}</p>
          </div>
        </div>
      </section>

      {/* LinkedIn connection */}
      <section className="rounded-xl border border-zinc-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">LinkedIn connection</h2>
            <p className="mt-0.5 text-xs text-zinc-500">
              {extInstalled
                ? `Extension connected · v${extVersion || '?'}`
                : 'Extension not detected'}
            </p>
            {linkedinUrl && (
              <p className="mt-1 text-xs text-zinc-400">
                Linked:{' '}
                <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {linkedinUrl.replace('https://www.', '')}
                </a>
              </p>
            )}
          </div>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${extInstalled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {extInstalled ? 'Connected' : 'Disconnected'}
          </span>
        </div>
      </section>

      {/* Email change */}
      <section className="rounded-xl border border-zinc-200 bg-white p-6">
        <h2 className="text-sm font-semibold text-zinc-900">Change email</h2>
        <p className="mt-0.5 text-xs text-zinc-500">
          We'll send confirmation links to <strong>both</strong> your current and new email addresses. You need to click the link in each to complete the change.
        </p>
        {emailStage === 'sent' ? (
          <div className="mt-3 space-y-2">
            <div className="rounded-md bg-green-50 px-3 py-2 text-xs text-green-700">
              Confirmation sent to <strong>{newEmail}</strong> and <strong>{user?.email}</strong>. Click the link in both to finish.
            </div>
            <button onClick={resetEmailFlow} className="text-xs text-blue-600 hover:underline">
              Use a different email
            </button>
          </div>
        ) : emailStage === 'confirmed' ? (
          <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-xs text-green-700">
            Email updated to <strong>{user?.email}</strong>.
          </div>
        ) : (
          <form
            className="mt-3 flex gap-2"
            onSubmit={(e) => { e.preventDefault(); handleEmailChange() }}
          >
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="new@email.com"
              maxLength={254}
              className="flex-1 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm focus:border-zinc-400 focus:outline-none"
            />
            <button
              type="submit"
              disabled={!newEmail.trim() || newEmail.trim().toLowerCase() === user?.email?.toLowerCase() || emailBusy}
              className="rounded-md bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-40"
            >
              {emailBusy ? 'Sending...' : 'Send confirmation'}
            </button>
          </form>
        )}
        {emailError && <p className="mt-2 text-xs text-red-500">{emailError}</p>}
      </section>

      {/* Sign out */}
      <section className="rounded-xl border border-zinc-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Sign out</h2>
            <p className="mt-0.5 text-xs text-zinc-500">Ends your session on this device and disconnects the extension.</p>
          </div>
          <button
            onClick={signOut}
            className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
          >
            Sign out
          </button>
        </div>
      </section>

      {/* Download my data — GDPR data-subject access request surface.
          Always visible, not feature-gated. See docs/adr/0016-privacy-controls.md. */}
      <section className="rounded-xl border border-zinc-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Download my data</h2>
            <p className="mt-0.5 text-xs text-zinc-500">
              Get a JSON bundle of everything Slash stores about you on our servers — templates, tags, reminders, contacts, team memberships, audit history.
            </p>
          </div>
          <button
            onClick={async () => {
              setExportBusy(true)
              setExportError(null)
              const err = await downloadMyData()
              setExportBusy(false)
              if (err) setExportError(err.message)
            }}
            disabled={exportBusy}
            className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
          >
            {exportBusy ? 'Preparing...' : 'Download'}
          </button>
        </div>
        {exportError && (
          <p className="mt-2 text-xs text-red-500">Couldn't export: {exportError}</p>
        )}
      </section>

      {/* Delete-account UI intentionally absent — see ADR 0038. The RPC
          exists but auth.users cleanup isn't wired, and shipping
          half-deletion is worse than shipping none. "Download my data"
          above covers the adjacent GDPR path. */}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-zinc-900 px-4 py-2.5 shadow-lg">
          <span className="text-xs text-white">{toast}</span>
        </div>
      )}
    </div>
  )
}
